// Scene navigation modes. Three controllers share one OrbitControls instance:
//
//   orbit     — standard target-locked orbit (drag to orbit, W/S dolly, A/D
//               azimuth, Q/E elevation, wheel zoom-to-cursor). Level, no roll.
//               Default.
//   turntable — orbit that slowly auto-spins (OrbitControls.autoRotate);
//               interaction pauses the spin and it resumes after. Level.
//   fly       — 6DOF first-person. OrbitControls is released (enabled=false)
//               and the render loop skips controls.update() (it would snap the
//               camera back to the orbit spherical otherwise — see
//               navModeUsesOrbitControls / shouldUpdateControls). Look is
//               pointer-lock only, driven from bootstrap/input.ts.
//
// Mirrors the cameraProjection param/intent/persistence pattern. setNavMode is
// the single mutation point; controllers are pure switches over the live
// camera/controls the loop reads each frame.

import * as THREE from 'three';
import { params } from '@store/instance';
import type { NavMode } from '@store/schema';
import type { ViewerContext } from './context';

const LS_KEY = 'magviz:navMode:v1';
// Modes the N key / badge click cycle through. Turntable is deliberately
// excluded — it's a presentation mode reachable only via the command palette
// (⌘K) or console (setNavMode), not the quick toggle. cycleNavMode handles a
// current mode that isn't in this list (e.g. turntable set via the palette).
const CYCLE_ORDER: readonly NavMode[] = ['orbit', 'fly'] as const;

// Rotate rate is per-second (dt-scaled). The pre-refactor keyboard path used
// per-frame 0.02 rad — ~1.2 rad/s at 60fps, preserved so orbit feels identical.
const ORBIT_ROT_PER_SEC = 1.2;
// Dolly is geometric: distance changes by this fraction per second (like the
// wheel's pow(0.95, …)), so zoom slows as it nears the target and never crosses
// minDistance — no fight with the per-frame controls.update() clamp. ~1.5 eases
// ~36 u/s at the default ~24 WU framing, close to the old 30 u/s linear feel.
const ORBIT_DOLLY_RATE = 1.5;

// Turntable idle spin. OrbitControls.autoRotateSpeed = 2.0 is ~30 s/rev at
// 60fps; 0.6 is a slow ~100 s/rev presentation spin.
const TURNTABLE_SPIN_SPEED = 0.6;

// Fly translation is gentler than the orbit dolly so you can thread through
// structure; Shift multiplies it for crossing the domain.
const FLY_SPEED_PER_SEC = 12;
const FLY_BOOST = 4;
const FLY_LOOK_SENS = 0.0026; // radians per pixel of pointer movement
const PITCH_LIMIT = (89 * Math.PI) / 180;

// Polar clamp keeps the turntable off the exact poles (gimbal degeneracy).
const POLAR_MIN = 0.05;
const POLAR_MAX = Math.PI - 0.05;

// On fly exit, re-pin the orbit pivot this far ahead of the camera so resuming
// orbit/turntable pivots around what you were looking at.
const EXIT_PIVOT_DIST = 15;

const WORLD_UP = new THREE.Vector3(0, 1, 0);

// Scratch vectors — module-level, reused per frame (no per-frame allocation).
const _dir = new THREE.Vector3();
const _right = new THREE.Vector3();
const _offset = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _sph = new THREE.Spherical();

export interface NavModeController {
    readonly mode: NavMode;
    /** Configure OrbitControls + camera.up for this mode. Idempotent. */
    enter(viewer: ViewerContext): void;
    /** Undo anything mode-specific before another mode enters. */
    exit(viewer: ViewerContext): void;
    /** Per-frame keyboard step, dt in seconds. */
    handleKeyboard(viewer: ViewerContext, dt: number): void;
}

function clamp(x: number, lo: number, hi: number): number {
    return x < lo ? lo : x > hi ? hi : x;
}

function shiftHeld(keys: Record<string, boolean>): boolean {
    return !!(keys['ShiftLeft'] || keys['ShiftRight']);
}

// Geometric dolly toward (signedExp < 0) / away from (> 0) the orbit target,
// clamped to OrbitControls' distance limits. The step is a fraction of the
// current distance, so it slows as it nears the target and never crosses
// minDistance — so the per-frame controls.update() clamp never fights it (the
// source of the hold-to-zoom jitter). lookAt is left to the following update().
function dollyTowardTarget(
    camera: THREE.Object3D,
    controls: { target: THREE.Vector3; minDistance: number; maxDistance: number } | null | undefined,
    target: THREE.Vector3,
    signedExp: number,
): void {
    _offset.copy(camera.position).sub(target);
    const dist = _offset.length();
    if (dist < 1e-6) return;
    const minD = controls ? controls.minDistance : 0;
    const maxD = controls ? controls.maxDistance : Number.POSITIVE_INFINITY;
    const newDist = clamp(dist * Math.exp(signedExp), minD, maxD);
    camera.position.copy(target).addScaledVector(_offset, newDist / dist);
}

// --- orbit (standard, level) ---------------------------------------------

const orbit: NavModeController = {
    mode: 'orbit',
    enter(viewer) {
        const c = viewer.three.controls;
        if (c) { c.enabled = true; c.enableRotate = true; c.autoRotate = false; }
    },
    exit() { /* nothing mode-specific */ },
    handleKeyboard(viewer, dt) {
        const camera = viewer.three.camera;
        const controls = viewer.three.controls;
        const keys = viewer.input.keys;
        if (!camera) return;
        const target = controls ? controls.target : new THREE.Vector3();
        const rot = ORBIT_ROT_PER_SEC * dt;

        let dollyExp = 0;
        if (keys['KeyW']) dollyExp -= ORBIT_DOLLY_RATE * dt;
        if (keys['KeyS']) dollyExp += ORBIT_DOLLY_RATE * dt;
        if (dollyExp !== 0) dollyTowardTarget(camera, controls, target, dollyExp);
        if (keys['KeyA']) {
            _offset.copy(camera.position).sub(target).applyAxisAngle(WORLD_UP, -rot);
            camera.position.copy(target).add(_offset); camera.lookAt(target);
        }
        if (keys['KeyD']) {
            _offset.copy(camera.position).sub(target).applyAxisAngle(WORLD_UP, rot);
            camera.position.copy(target).add(_offset); camera.lookAt(target);
        }
        if (keys['KeyQ']) {
            _axis.set(1, 0, 0).applyQuaternion(camera.quaternion).normalize();
            _offset.copy(camera.position).sub(target).applyAxisAngle(_axis, rot);
            camera.position.copy(target).add(_offset); camera.lookAt(target);
        }
        if (keys['KeyE']) {
            _axis.set(1, 0, 0).applyQuaternion(camera.quaternion).normalize();
            _offset.copy(camera.position).sub(target).applyAxisAngle(_axis, -rot);
            camera.position.copy(target).add(_offset); camera.lookAt(target);
        }
    },
};

// --- turntable (level orbit + slow auto-spin) ----------------------------

const turntable: NavModeController = {
    mode: 'turntable',
    enter(viewer) {
        const c = viewer.three.controls;
        const camera = viewer.three.camera;
        if (camera) camera.up.set(0, 1, 0);
        if (c) {
            c.enabled = true;
            c.enableRotate = true;
            c.autoRotate = true; // hands-free presentation spin; pauses during interaction
            c.autoRotateSpeed = TURNTABLE_SPIN_SPEED;
            c.screenSpacePanning = false; // pan stays in the ground plane
            c.update();
        }
    },
    exit(viewer) {
        const c = viewer.three.controls;
        if (c) { c.autoRotate = false; c.screenSpacePanning = true; }
    },
    handleKeyboard(viewer, dt) {
        const camera = viewer.three.camera;
        const controls = viewer.three.controls;
        const keys = viewer.input.keys;
        if (!camera || !controls) return;
        const rot = ORBIT_ROT_PER_SEC * dt;

        const target = controls.target;
        let dollyExp = 0;
        if (keys['KeyW']) dollyExp -= ORBIT_DOLLY_RATE * dt;
        if (keys['KeyS']) dollyExp += ORBIT_DOLLY_RATE * dt;
        if (dollyExp !== 0) dollyTowardTarget(camera, controls, target, dollyExp);
        // Azimuth/polar nudges via spherical coords about the target, polar
        // clamped off the poles. Recomputing from a +Y-referenced Spherical
        // (up pinned to +Y) keeps the horizon level — no roll.
        if (keys['KeyA'] || keys['KeyD'] || keys['KeyQ'] || keys['KeyE']) {
            _sph.setFromVector3(_offset.copy(camera.position).sub(target));
            if (keys['KeyA']) _sph.theta -= rot;
            if (keys['KeyD']) _sph.theta += rot;
            if (keys['KeyQ']) _sph.phi = clamp(_sph.phi - rot, POLAR_MIN, POLAR_MAX);
            if (keys['KeyE']) _sph.phi = clamp(_sph.phi + rot, POLAR_MIN, POLAR_MAX);
            _sph.makeSafe();
            camera.position.copy(target).add(_offset.setFromSpherical(_sph));
            camera.up.set(0, 1, 0);
            camera.lookAt(target);
        }
    },
};

// --- fly (6DOF first-person, pointer-lock) -------------------------------

const flyState = { yaw: 0, pitch: 0 };

function seedFlyFromCamera(camera: THREE.Object3D): void {
    _euler.setFromQuaternion(camera.quaternion, 'YXZ');
    flyState.yaw = _euler.y;
    flyState.pitch = clamp(_euler.x, -PITCH_LIMIT, PITCH_LIMIT);
}

const fly: NavModeController = {
    mode: 'fly',
    enter(viewer) {
        const camera = viewer.three.camera;
        const controls = viewer.three.controls;
        if (camera) { camera.up.set(0, 1, 0); seedFlyFromCamera(camera); }
        // Released so OrbitControls stops driving the camera. The loop also
        // skips controls.update() while in fly mode (shouldUpdateControls).
        if (controls) controls.enabled = false;
    },
    exit(viewer) {
        const camera = viewer.three.camera;
        const controls = viewer.three.controls;
        if (!camera || !controls) return;
        // Re-pin the pivot ahead of the camera so orbit/turntable resume around
        // what the user was looking at, then hand control back.
        camera.getWorldDirection(_dir);
        const d = clamp(EXIT_PIVOT_DIST, controls.minDistance, controls.maxDistance);
        controls.target.copy(camera.position).addScaledVector(_dir, d);
        controls.enabled = true;
        controls.update();
    },
    handleKeyboard(viewer, dt) {
        const camera = viewer.three.camera;
        const keys = viewer.input.keys;
        if (!camera) return;
        const speed = (shiftHeld(keys) ? FLY_SPEED_PER_SEC * FLY_BOOST : FLY_SPEED_PER_SEC) * dt;
        camera.getWorldDirection(_dir);
        _right.setFromMatrixColumn(camera.matrix, 0); // camera local X (right)

        if (keys['KeyW']) camera.position.addScaledVector(_dir, speed);
        if (keys['KeyS']) camera.position.addScaledVector(_dir, -speed);
        if (keys['KeyD']) camera.position.addScaledVector(_right, speed);
        if (keys['KeyA']) camera.position.addScaledVector(_right, -speed);
        if (keys['KeyE']) camera.position.addScaledVector(WORLD_UP, speed);
        if (keys['KeyQ']) camera.position.addScaledVector(WORLD_UP, -speed);
    },
};

/** Re-seed fly yaw/pitch from the live camera at the start of a look session,
 *  so any camera change since the last one (preset snap, gnomon, R) is absorbed
 *  rather than producing a jump on the next pointer movement. */
export function flyBeginLook(viewer: ViewerContext): void {
    const camera = viewer.three.camera;
    if (camera) seedFlyFromCamera(camera);
}

/** Apply a pointer-movement delta (pixels) to the fly camera's look direction.
 *  No-op unless the camera exists; clamps pitch to avoid flipping the poles. */
export function flyApplyLook(viewer: ViewerContext, dxPixels: number, dyPixels: number): void {
    const camera = viewer.three.camera;
    if (!camera) return;
    flyState.yaw -= dxPixels * FLY_LOOK_SENS;
    flyState.pitch = clamp(flyState.pitch - dyPixels * FLY_LOOK_SENS, -PITCH_LIMIT, PITCH_LIMIT);
    _euler.set(flyState.pitch, flyState.yaw, 0, 'YXZ');
    camera.quaternion.setFromEuler(_euler);
}

const CONTROLLERS: Record<NavMode, NavModeController> = { orbit, turntable, fly };

let activeMode: NavMode = 'orbit';

export function getNavController(mode: NavMode): NavModeController {
    return CONTROLLERS[mode];
}

export function getActiveController(): NavModeController {
    return CONTROLLERS[activeMode];
}

/** Persisted nav mode (defaults to orbit on any read failure). */
export function loadNavMode(): NavMode {
    try {
        const v = localStorage.getItem(LS_KEY);
        return v === 'turntable' || v === 'fly' ? v : 'orbit';
    } catch {
        return 'orbit';
    }
}

function saveNavMode(mode: NavMode): void {
    try { localStorage.setItem(LS_KEY, mode); } catch { /* quota / private mode */ }
}

/** Boot-time: adopt the persisted mode and run its enter() once (no exit). */
export function initNavMode(viewer: ViewerContext): void {
    activeMode = loadNavMode();
    params.navMode = activeMode;
    CONTROLLERS[activeMode].enter(viewer);
}

/** Switch modes: exit the old, enter the new, persist, sync the param. */
export function setNavMode(viewer: ViewerContext, mode: NavMode): void {
    if (mode === activeMode) return;
    CONTROLLERS[activeMode].exit(viewer);
    activeMode = mode;
    CONTROLLERS[mode].enter(viewer);
    params.navMode = mode;
    saveNavMode(mode);
}

/** Toggle orbit ↔ fly. Turntable is skipped (palette/console only); from
 *  turntable, indexOf is -1 so the first entry (orbit) is next. */
export function cycleNavMode(viewer: ViewerContext): void {
    const idx = CYCLE_ORDER.indexOf(activeMode);
    const next = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length]!;
    setNavMode(viewer, next);
}

/** Re-apply the active mode's enter() — used after a projection swap recreates
 *  camera + controls, so fly's enabled=false (etc.) is restored. */
export function reapplyNavMode(viewer: ViewerContext): void {
    CONTROLLERS[activeMode].enter(viewer);
}

/** The loop calls this to decide whether to run controls.update() — fly mode
 *  must skip it or OrbitControls snaps the camera back to the orbit spherical. */
export function navModeUsesOrbitControls(): boolean {
    return activeMode !== 'fly';
}
